/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import type { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ToastProvider } from '@/design-system';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider } from '@/auth/AuthProvider';
import { ErrorBoundary } from './ErrorBoundary';

/**
 * The kit's inverse toast stack, bottom-centre. `avoidFab` lifts it above the feedback button
 * below 600px, where a centred toast spans the screen.
 */
function AppToasts({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <ToastProvider avoidFab labels={{ dismiss: t('toast.dismiss') }}>
      {children}
    </ToastProvider>
  );
}

/** Composes all app-wide providers (error boundary, auth, server-state cache, toasts). */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppToasts>{children}</AppToasts>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
